import { PlusIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import Button from '../../../components/tailwind/Button'
import { Heading } from '../../../components/ui/Heading'
import { useUserContext } from '../../../context'
import { useDiscover } from '../hooks/useDiscover'
import { useSelectionParams } from '../hooks/useSelectionParams'
import SelectionCareerCard from './flexibleMatching/SelectionCareerCard'
import SelectionTalentCard from './flexibleMatching/SelectionTalentCard'

const SelectionSection = ({
  title,
  items,
  renderItem,
  addMoreTitle,
  onClickAddMore,
  disabled = false,
  isLoading
}) => (
  <div className='flex flex-col gap-y-4'>
    <Heading level={3} className='text-darkBlue'>
      {isLoading ? (
        <Skeleton className='max-w-64' />
      ) : (
        `${title} (${items.length})`
      )}
    </Heading>
    {!isLoading && (
      <div className='flex flex-row flex-wrap gap-2'>
        {items.map(renderItem)}
        <Button.SecondaryXL
          text={addMoreTitle}
          onClick={onClickAddMore}
          disabled={disabled}
          icon={<PlusIcon className='size-4 text-buttonSecondaryBlue' />}
          className='h-fit flex-none self-center'
        />
      </div>
    )}
  </div>
)

SelectionSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  addMoreTitle: PropTypes.string.isRequired,
  onClickAddMore: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default function SelectionOverviewSection({
  className,
  selectedTalents,
  selectedCareers
}) {
  const { t } = useTranslation()
  const { setInviteTalentOpen, setAddCareerModalOpen } = useDiscover()
  const { activeSpace } = useUserContext()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding
  const { removeCareer, removeTalent } = useSelectionParams()

  const renderTalentCard = useCallback(
    (talent, index) => (
      <SelectionTalentCard
        key={index}
        talentOwnerId={talent}
        onClickClose={() => removeTalent(talent)}
      />
    ),
    [removeTalent]
  )

  const renderCareerCard = useCallback(
    (career, index) => (
      <SelectionCareerCard
        key={index}
        careerId={career}
        onClickClose={() => removeCareer(career)}
      />
    ),
    [removeCareer]
  )

  return (
    <div className={clsx('w-full rounded-lg bg-white p-6', className)}>
      <div className='space-y-6'>
        <SelectionSection
          title={t('discover.flexible_matching_section.selected_talents')}
          items={selectedTalents}
          renderItem={renderTalentCard}
          addMoreTitle={t('discover.flexible_matching_section.add_talent')}
          onClickAddMore={() => setInviteTalentOpen(true)}
          disabled={!hasCompletedOnboarding}
        />
        <SelectionSection
          title={t('discover.flexible_matching_section.selected_careers')}
          items={selectedCareers}
          renderItem={renderCareerCard}
          addMoreTitle={t('discover.flexible_matching_section.add_career')}
          onClickAddMore={() => setAddCareerModalOpen(true)}
          disabled={!hasCompletedOnboarding}
        />
      </div>
    </div>
  )
}

SelectionOverviewSection.propTypes = {
  className: PropTypes.string,
  selectedTalents: PropTypes.array.isRequired,
  selectedCareers: PropTypes.array.isRequired
}
